/**
 * Admin workspaces list page for /admin/workspaces route.
 * Lists all workspaces in the platform for admin management.
 * Uses KiiList with server-side search, filters, and pagination.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { useAdminWorkspaces, useAdminWorkspaceActivity } from "../hooks/useAdminWorkspaces";
import { useProfile } from "../contexts/ProfileContext";
import { useDateFormatter } from "../hooks/useDateFormatter";
import { Building2, Users, FolderKanban, Plus, Loader2, X, Search } from "lucide-react";
import { KiiList, KiiDialogLayout, type KiiListColumn, type FilterConfig, type FilterValues } from "../components/kii";
import { TableRow, TableCell } from "../components/ui/table";
import { createWorkspace, adminGetAllUsers, type AdminWorkspace, type AdminUser } from "../services/naraDoor";
import type { TFunction } from "i18next";
import { publicRoutes } from "../lib/routes";

const dotClass = "h-2 w-2 rounded-full shrink-0";

function Dot({ color, label }: { color: string; label: string }) {
  return (
    <span title={label} className={`${dotClass} ${color}`}>
      <span className="sr-only">{label}</span>
    </span>
  );
}

function getStatusBadge(workspace: AdminWorkspace, t: TFunction) {
  if (workspace.deleted_at) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground">
        {t("adminWorkspaces.status.deleted")}
      </span>
    );
  }
  if (workspace.is_blocked) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300">
        {t("adminWorkspaces.status.blocked")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
      {t("adminWorkspaces.status.active")}
    </span>
  );
}

const columns: KiiListColumn[] = [
  { key: "name", label: "Name" },
  { key: "handle", label: "Handle" },
  { key: "members", label: "Members" },
  { key: "projects", label: "Projects" },
  { key: "status", label: "Status" },
  { key: "dots", label: "" },
  { key: "created", label: "Created" },
];

const statusFilterOptions = [
  { value: "active", label: "Active" },
  { value: "blocked", label: "Blocked" },
  { value: "deleted", label: "Deleted" },
];

const statusFilters: FilterConfig[] = [
  {
    id: "status",
    label: "Status",
    placeholder: "All statuses",
    colorVariant: "primary",
    multiple: false,
    options: statusFilterOptions,
  },
];

export function AdminWorkspaces() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const { formatDate } = useDateFormatter();

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterValues, setFilterValues] = useState<FilterValues>({ status: null });
  const [skip, setSkip] = useState(0);
  const limit = 20;

  // Create workspace dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newHandle, setNewHandle] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Owner selector state
  const [ownerSearch, setOwnerSearch] = useState("");
  const [ownerResults, setOwnerResults] = useState<AdminUser[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<AdminUser | null>(null);
  const [searchingOwners, setSearchingOwners] = useState(false);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const ownerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ownerDropdownRef = useRef<HTMLDivElement>(null);

  // Close owner dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(e.target as Node)) {
        setShowOwnerDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced owner search
  const handleOwnerSearchChange = useCallback((query: string) => {
    setOwnerSearch(query);
    if (ownerSearchTimer.current) clearTimeout(ownerSearchTimer.current);
    if (!query.trim()) {
      setOwnerResults([]);
      setShowOwnerDropdown(false);
      return;
    }
    ownerSearchTimer.current = setTimeout(async () => {
      setSearchingOwners(true);
      try {
        const res = await adminGetAllUsers(0, 10, query.trim());
        setOwnerResults(res.users);
        setShowOwnerDropdown(true);
      } catch {
        setOwnerResults([]);
      } finally {
        setSearchingOwners(false);
      }
    }, 300);
  }, []);

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const {
    data,
    isLoading,
  } = useAdminWorkspaces({
    skip,
    limit,
    search: search || undefined,
    includeBlocked: true,
  });

  // Boosts bought in the last 30 days: the same "paying" meaning as the Momo wall.
  const { data: activity, error: activityError } = useAdminWorkspaceActivity(30);
  useEffect(() => {
    if (activityError) console.error("AdminWorkspaces: payment activity load failed", activityError);
  }, [activityError]);
  const paidWorkspaceIds = new Set(
    (activity?.workspaces ?? []).filter((a) => a.boostsPurchased > 0).map((a) => a.workspaceId)
  );

  // Client-side status filter (API returns all, we filter by status)
  const allWorkspaces = data?.workspaces || [];
  const statusFilter = filterValues.status as string | null;

  const workspaces = statusFilter
    ? allWorkspaces.filter((ws) => {
        if (statusFilter === "deleted") return !!ws.deleted_at;
        if (statusFilter === "blocked") return !ws.deleted_at && ws.is_blocked;
        if (statusFilter === "active") return !ws.deleted_at && !ws.is_blocked;
        return true;
      })
    : allWorkspaces;

  const total = data?.total || 0;

  const handleSearch = useCallback((query: string) => {
    setSearch(query.trim());
    setSkip(0);
  }, []);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchInput(query);
  }, []);

  const handleFilterChange = useCallback((newFilters: FilterValues) => {
    setFilterValues(newFilters);
  }, []);

  const handlePageChange = useCallback((newSkip: number) => {
    setSkip(newSkip);
  }, []);

  const handleRowClick = useCallback((workspace: AdminWorkspace) => {
    navigate(`/admin/workspaces/${workspace.id}`);
  }, [navigate]);

  const handleCreateWorkspace = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const ws = await createWorkspace({
        name: newName.trim(),
        handle: newHandle.trim() || undefined,
        ownerUserId: selectedOwner?.id,
      });
      setCreateDialogOpen(false);
      setNewName("");
      setNewHandle("");
      setSelectedOwner(null);
      setOwnerSearch("");
      setOwnerResults([]);
      queryClient.invalidateQueries({ queryKey: ["adminWorkspaces"] });
      navigate(`/admin/workspaces/${ws.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create workspace");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("adminWorkspaces.title")}
        description={t("adminWorkspaces.description")}
        isAdmin
        breadcrumbs={[
          { label: t("adminWorkspaces.breadcrumbs.dashboard"), href: publicRoutes.home },
          { label: t("adminWorkspaces.breadcrumbs.admin") },
          { label: t("adminWorkspaces.breadcrumbs.workspaces") },
        ]}
        actions={
          <button
            onClick={() => setCreateDialogOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-nara-orange text-nara-charcoal font-medium rounded-lg hover:bg-nara-orange/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Workspace
          </button>
        }
      />

      {activityError && (
        <p className="mb-3 text-sm text-red-600">{t("adminWorkspaces.dot.loadError")}</p>
      )}

      <KiiList<AdminWorkspace>
        filters={statusFilters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        searchPlaceholder={t("adminWorkspaces.searchPlaceholder")}
        searchQuery={searchInput}
        onSearchQueryChange={handleSearchQueryChange}
        onSearch={handleSearch}
        columns={columns}
        data={workspaces}
        isLoading={isLoading}
        emptyMessage={search ? t("adminWorkspaces.noWorkspacesFound") : t("adminWorkspaces.noWorkspacesYet")}
        emptyIcon={<Building2 className="h-10 w-10 text-muted-foreground" />}
        showCount={true}
        countLabel={(count) => t("adminWorkspaces.workspaceCount", { count })}
        onRowClick={handleRowClick}
        serverPagination={{
          totalCount: total,
          skip,
          limit,
        }}
        onPageChange={handlePageChange}
        renderRow={(workspace) => (
          <TableRow key={workspace.id}>
            <TableCell>
              <div className="flex items-center gap-3">
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium">{workspace.name}</span>
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {workspace.handle ? `@${workspace.handle}` : "\u2014"}
            </TableCell>
            <TableCell className="text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {workspace.member_count ?? "\u2014"}
              </span>
            </TableCell>
            <TableCell className="text-muted-foreground">
              <span className="flex items-center gap-1">
                <FolderKanban className="h-3.5 w-3.5" />
                {workspace.project_count ?? "\u2014"}
              </span>
            </TableCell>
            <TableCell>
              {getStatusBadge(workspace, t)}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1.5">
                {workspace.is_dev && <Dot color="bg-pink-500" label={t("adminWorkspaces.dot.dev")} />}
                {(workspace.billing_hidden || paidWorkspaceIds.has(workspace.id)) && (
                  <Dot color="bg-green-500" label={t("adminWorkspaces.dot.paying")} />
                )}
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatDate(workspace.created_at)}
            </TableCell>
          </TableRow>
        )}
      />

      {/* Create Workspace Dialog */}
      <KiiDialogLayout
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        title="Create Workspace"
        description="Create a new workspace for organizing projects."
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setCreateDialogOpen(false)}
              className="px-4 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateWorkspace}
              disabled={creating || !newName.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-nara-orange text-nara-charcoal font-medium rounded-lg hover:bg-nara-orange/90 transition-colors disabled:opacity-50"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              {creating ? "Creating..." : "Create Workspace"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="My Company"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) handleCreateWorkspace();
              }}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Handle <span className="text-muted-foreground text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={newHandle}
              onChange={(e) => setNewHandle(e.target.value)}
              placeholder="my-company"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Letters, numbers, and hyphens only.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Owner <span className="text-muted-foreground text-xs">(optional)</span>
            </label>
            {selectedOwner ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-background">
                <span className="text-sm text-foreground flex-1">
                  {selectedOwner.first_name} {selectedOwner.last_name}
                  {selectedOwner.email && (
                    <span className="text-muted-foreground ml-1">({selectedOwner.email})</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedOwner(null);
                    setOwnerSearch("");
                    setOwnerResults([]);
                  }}
                  className="p-0.5 rounded hover:bg-accent transition-colors"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            ) : (
              <div className="relative" ref={ownerDropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={ownerSearch}
                    onChange={(e) => handleOwnerSearchChange(e.target.value)}
                    placeholder="Search users by name or email..."
                    className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                    onFocus={() => {
                      if (ownerResults.length > 0) setShowOwnerDropdown(true);
                    }}
                  />
                  {searchingOwners && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                {showOwnerDropdown && ownerResults.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto border border-border rounded-lg bg-background shadow-lg">
                    {ownerResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setSelectedOwner(user);
                          setOwnerSearch("");
                          setOwnerResults([]);
                          setShowOwnerDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                      >
                        <span className="font-medium">{user.first_name} {user.last_name}</span>
                        {user.email && (
                          <span className="text-muted-foreground ml-1">({user.email})</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {showOwnerDropdown && ownerSearch.trim() && !searchingOwners && ownerResults.length === 0 && (
                  <div className="absolute z-50 mt-1 w-full border border-border rounded-lg bg-background shadow-lg px-3 py-2 text-sm text-muted-foreground">
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>
          {createError && (
            <p className="text-sm text-red-500">{createError}</p>
          )}
        </div>
      </KiiDialogLayout>
    </div>
  );
}
